import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Input,
} from "reactstrap";
import HolidayTypeTable from "../holiday-type/HolidayTypeTable";
import HolidayTypeFormModal from "../holiday-type/HolidayTypeFormModal";
import ConfirmDeleteModal from "../holiday-type/ConfirmDeleteModal";
import CommonDropDown from "../common/CommonDropDown";
import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
  import { useTranslation } from "react-i18next";
 import SearchBar from "../../common-elements/search-bar/SearchBar";
import Breadcrumbs from "../../common-elements/breadcrumbs/Breadcrumbs";

export interface HolidayType {
  id: number;
  typeName: string;
  description: string;
}

const initialData: HolidayType[] = [
  {
    id: 1,
    typeName: "National Holiday",
    description:
      "Holidays of national significance, e.g., Republic Day, Independence Day",
  },
  {
    id: 2,
    typeName: "Religious Holiday",
    description:
      "Holidays based on religious observances, e.g., Good Friday, Diwali, Eid",
  },
  {
    id: 3,
    typeName: "Cultural Holiday",
    description:
      "Holidays celebrating cultural events or traditions, e.g., Holi, Raksha Bandhan",
  },
  {
    id: 4,
    typeName: "Public Holiday",
    description:
      "General holidays observed by the public sector, e.g., May Day, Boxing Day",
  },
  {
    id: 5,
    typeName: "Regional Holiday",
    description:
      "Holidays specific to certain regions or states, e.g., Onam, Pongal",
  },
  {
    id: 6,
    typeName: "Optional Holiday",
    description:
      "Holidays that are optional for employees to take, e.g., local festivals, personal observances",
  },
];

const HolidayTypeList: React.FC = () => {
  const [holidayTypes, setHolidayTypes] = useState<HolidayType[]>(initialData);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<HolidayType | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (data: HolidayType) => {
    setEditData(data);
    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
  };

  const handleModalSubmit = (data: Omit<HolidayType, "id">, id?: number) => {
    if (id) {
      setHolidayTypes((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...data } : item))
      );
    } else {
      setHolidayTypes((prev) => [
        ...prev,
        {
          id: prev.length ? Math.max(...prev.map((h) => h.id)) + 1 : 1,
          ...data,
        },
      ]);
    }
    setModalOpen(false);
    setEditData(null);
  };

  const confirmDelete = () => {
    if (deleteId !== null) {
      setHolidayTypes((prev) => prev.filter((item) => item.id !== deleteId));
      setDeleteId(null);
    }
  };

  // Filter holiday types by search text (type name or description)
  const filteredHolidayTypes = holidayTypes.filter(
    (item) =>
      item.typeName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase())
  );

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [modalOpen]);

  return (
    <>
      <Breadcrumbs
        mainTitle={t("HolidayType")}
        parent={t("Master Data")}
        parentLink={`${process.env.PUBLIC_URL}/masterdata/holiday-type`}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="list-product-header">
                  <div className="header-container">
                    {/* Search bar (left) */}
                    <SearchBar
                      label={t("Search")}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder={t("SearchByHolidayName")}
                    />
                    {/* Buttons (right) */}
                    <div className="actions-container">
                      <Link
                        className="btn btn-success"
                        to="/masterdata/import-holiday-type"
                      >
                        <i className="fa fa-download" /> {t("Import")}
                      </Link>
                      <CommonDropDown color="info" />
                      <div className="btn btn-primary" onClick={handleAdd}>
                        <i className="fa fa-plus" style={{ marginRight: 4 }} />{" "}
                        {t("AddHolidayType")}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="list-product">
                  <div className="table-responsive">
                    <HolidayTypeTable
                      data={filteredHolidayTypes}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <HolidayTypeFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        editData={editData}
      />
      <ConfirmDeleteModal
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        name={holidayTypes.find((item) => item.id === deleteId)?.typeName}
      />
    </>
  );
};

export default HolidayTypeList;
